"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useRef,
} from "react";
import { useRouter } from "next/navigation";
import { SessionUser } from "@/types/common";
import { userProfileService } from "@/services/userProfileService";

interface AuthContextType {
  sessionUser: SessionUser | null;
  setSessionUser: React.Dispatch<React.SetStateAction<SessionUser | null>>;
  logout: () => Promise<void>;
  fetchSession: () => Promise<void>;
  setSession: (
    userData: Partial<SessionUser> & { token?: string }
  ) => Promise<void>;
  sessionLoading: boolean;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const router = useRouter();
  const hasFetchedSession = useRef(false);


  const fetchSession = async () => {
    setSessionLoading(true);
    try {
      const token = localStorage.getItem('peoples_token') ?? "";
      if (!token) {
        return
      }
      const res = await userProfileService.getUserProfile(token);
      // console.log("res fetchSession", res?.data);
      // const res = await fetch("/api/getAccessToken", {
      //   method: "GET",
      //   cache: "no-store",
      //   headers: { "Content-Type": "application/json" },
      //   credentials: "include",
      // });

      if (!res.success) throw new Error("Session fetch failed");

      setSessionUser(res?.data || null);
      localStorage.setItem("peoples_token", res.data?.token);
      // Set cookie so middleware recognizes auth
      if (res.data?.token) {
        document.cookie = `peoples_token=${res.data.token}; Path=/; SameSite=Lax`;
      }
    } catch (err) {
      console.error("fetchSession error:", err);
      setSessionUser(null);
    } finally {
      setSessionLoading(false);
    }
  };

  useEffect(() => {
    if (!hasFetchedSession.current) {
      fetchSession();
      hasFetchedSession.current = true;
    }
  }, [sessionUser]);


  const setSession = async (
    // userData: Partial<SessionUser> & { token?: string }
  ): Promise<void> => {
    try {
      const token = localStorage.getItem('peoples_token') ?? "";
      if (!token) {
        return
      }
      const response = await userProfileService.setUserProfile(token);

      // console.log("response setSession", response);
      if (!response.success) throw new Error("Session get failed");

      setSessionUser(response.data?.user || null);
      localStorage.setItem("peoples_token", response.data?.token);
      if (response.data?.token) {
        document.cookie = `peoples_token=${response.data.token}; Path=/; SameSite=Lax`;
      }

    } catch (error) {
      console.error("setSession error:", error);
    }
  };

  const logout = async () => {
    try {
      // Clear all authentication data
      document.cookie = "website_session=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT";
      document.cookie = "peoples_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT";
      localStorage.setItem("logout", Date.now().toString());
      localStorage.removeItem("peoples_token");
      localStorage.clear();
      setSessionUser(null);
      console.log("🔓 [AUTH_CONTEXT] User logged out successfully");
      router.push("/");
    } catch (err) {
      console.error("logout error:", err);
      // Even if there's an error, still clear local data
      document.cookie = "peoples_token=; Path=/; Expires=Thu, 01 Jan 1970 00:00:00 GMT";
      localStorage.clear();
      setSessionUser(null);
      router.push("/");
    }
  };

  useEffect(() => {
    const syncLogout = (e: StorageEvent) => {
      if (e.key === "logout") {
        setSessionUser(null);
        // router.push("/");
        router.refresh()
      }
    };
    window.addEventListener("storage", syncLogout);
    return () => window.removeEventListener("storage", syncLogout);
  }, [router]);

  return (
    <AuthContext.Provider
      value={{ sessionUser, setSessionUser, logout, fetchSession, setSession, sessionLoading }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};
